import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { API, C, PRESENCE_INTERVAL } from './constants';
import { getGridCols, groupEpisodes } from './utils';
import { subscribeSSE } from './sse';
import useWindowSize from './hooks/useWindowSize';
import AuthScreen      from './components/AuthScreen';
import VideoPlayer     from './components/VideoPlayer';
import DetailModal     from './components/DetailModal';
import SeriesModal     from './components/SeriesModal';
import GenreRow        from './components/GenreRow';
import ContinueRow     from './components/ContinueRow';
import SeriesRow       from './components/SeriesRow';
import Section         from './components/Section';
import WatchlistRow    from './components/WatchlistRow';
import SearchResults   from './components/SearchResults';
import ChatBubble      from './components/chat/ChatBubble';
import AdminPanel      from './components/admin/AdminPanel';
import UploaderPanel   from './components/admin/UploaderPanel';
import ProfileSelector from './components/ProfileSelector';
import UserMenu        from './components/UserMenu';
import BottomNav       from './components/BottomNav';

export default function App() {
  const [token, setToken]         = useState(localStorage.getItem('token') || '');
  const [user, setUser]           = useState(JSON.parse(localStorage.getItem('user') || 'null'));
  const [profiles, setProfiles]   = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [library, setLibrary]     = useState([]);
  const [history, setHistory]     = useState({});
  const [mediaInfo, setMediaInfo] = useState({});
  const [ratings, setRatings]     = useState({});
  const [selected, setSelected]   = useState(null);
  const [selectedSubs, setSelectedSubs] = useState([]);
  const [detail, setDetail]       = useState(null);
  const [search, setSearch]       = useState('');
  const [filter, setFilter]       = useState('all');
  const [hovered, setHovered]     = useState(null);
  const [showAdmin, setShowAdmin]             = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [installPrompt, setInstallPrompt]     = useState(null);
  const [transcodeStatus, setTranscodeStatus] = useState({});
  const [seriesList, setSeriesList]           = useState([]);
  const [selectedSeries, setSelectedSeries]   = useState(null);
  const [watchlist, setWatchlist]             = useState(new Set());
  const [notification, setNotification]       = useState(null);
  // Búsqueda server-side
  const [searchResults, setSearchResults]     = useState(null);
  const [searchLoading, setSearchLoading]     = useState(false);
  const [genreFilter, setGenreFilter]         = useState('');
  const [yearFilter, setYearFilter]           = useState('');
  const [availableGenres, setAvailableGenres] = useState([]);
  const [upNextSecs, setUpNextSecs]           = useState(null);
  const videoRef       = useRef(null);
  const progressTimer  = useRef(null);
  const presenceTimer  = useRef(null);
  const watchingFile   = useRef('');
  const notifTimer     = useRef(null);
  const searchTimer    = useRef(null);
  const searchAbort    = useRef(null);
  const { width }      = useWindowSize();

  const isMobile = width < 550;
  const isTablet = width >= 550 && width < 1024;
  const isTV     = width >= 1600;
  const cols     = getGridCols(width);

  // Hay búsqueda o filtro activo → mostrar resultados del servidor
  const isSearchMode = search.length > 0 || genreFilter !== '' || yearFilter !== '';

  // PWA install prompt
  useEffect(() => {
    const handler = e => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setInstallPrompt(null));
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') setInstallPrompt(null);
  };

  // Profile helpers
  const profileHeaders = useCallback((extra = {}) => ({
    Authorization: `Bearer ${token}`,
    ...(selectedProfile ? { 'X-Profile-ID': String(selectedProfile.id) } : {}),
    ...extra,
  }), [token, selectedProfile]);

  const handleSelectProfile = useCallback((p) => {
    setSelectedProfile(p);
    localStorage.setItem('profile_id', String(p.id));
  }, []);

  const onAuth = (t, u, profs) => {
    setToken(t);
    setUser(u);
    if (Array.isArray(profs) && profs.length > 0) {
      setProfiles(profs);
      setSelectedProfile(null);
    }
  };

  const logout = useCallback(() => {
    fetch(`${API}/auth/logout`, { method:'POST', headers:{ Authorization:`Bearer ${token}` } });
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('profile_id');
    setToken(''); setUser(null); setLibrary([]); setHistory({});
    setProfiles([]); setSelectedProfile(null);
    clearInterval(presenceTimer.current);
  }, [token]);

  // Sesión vencida → volver al login en vez de mostrar la biblioteca vacía.
  const authJson = useCallback(r => {
    if (r.status === 401) { logout(); throw new Error('unauthorized'); }
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  }, [logout]);

  // fetch con token + perfil que vuelve al login si la sesión venció.
  const apiJson = useCallback((url, opts = {}) =>
    fetch(url, { ...opts, headers: profileHeaders(opts.headers) }).then(authJson),
  [profileHeaders, authJson]);

  // El rol real viene del servidor; el de localStorage solo sirve para el primer render.
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/me`, { headers:{ Authorization:`Bearer ${token}` } })
      .then(authJson)
      .then(u => { if (u && u.id) { setUser(u); localStorage.setItem('user', JSON.stringify(u)); } })
      .catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadLibrary = useCallback(() => {
    fetch(`${API}/api/library`, { headers:{ Authorization:`Bearer ${token}` } })
      .then(authJson).then(d => { if (d) setLibrary(Array.isArray(d) ? d : []); }).catch(() => {});
  }, [token, authJson]);

  const reloadRatings = useCallback(() => {
    fetch(`${API}/api/ratings`, { headers:{ Authorization:`Bearer ${token}` } })
      .then(authJson).then(d => { if (d) setRatings(d); }).catch(() => {});
  }, [token, authJson]);

  const reloadInfo = useCallback(() => {
    fetch(`${API}/api/media-info`, { headers:{ Authorization:`Bearer ${token}` } })
      .then(authJson).then(d => { if (d) setMediaInfo(d); }).catch(() => {});
  }, [token, authJson]);

  const reloadSeries = useCallback(() => {
    fetch(`${API}/api/series`, { headers:{ Authorization:`Bearer ${token}` } })
      .then(authJson).then(d => { if (Array.isArray(d)) setSeriesList(d); }).catch(() => {});
  }, [token, authJson]);

  const reloadWatchlist = useCallback(() => {
    apiJson(`${API}/api/watchlist`)
      .then(d => { if (Array.isArray(d)) setWatchlist(new Set(d)); }).catch(() => {});
  }, [token, selectedProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleWatchlist = useCallback(async (key) => {
    const inList = watchlist.has(key);
    setWatchlist(prev => { const next = new Set(prev); inList ? next.delete(key) : next.add(key); return next; });
    try {
      const res = inList
        ? await fetch(`${API}/api/watchlist?file=${encodeURIComponent(key)}`, { method:'DELETE', headers: profileHeaders() })
        : await fetch(`${API}/api/watchlist`, { method:'POST', headers: profileHeaders({ 'Content-Type':'application/json' }), body: JSON.stringify({ file_name: key }) });
      if (!res.ok) reloadWatchlist();
    } catch { reloadWatchlist(); }
  }, [watchlist, token, selectedProfile, reloadWatchlist]); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadTranscodeStatus = useCallback(() => {
    if (!token || library.length === 0) return;
    // Una sola petición con el estado de todos los archivos (antes era una por título).
    fetch(`${API}/api/transcode-status`, { headers:{ Authorization:`Bearer ${token}` } })
      .then(authJson)
      .then(all => {
        const map = {};
        library.forEach(m => { map[m.file] = all[m.file] || 'pending'; });
        setTranscodeStatus(map);
      })
      .catch(() => {});
  }, [token, library, authJson]);

  const preloadedRef = useRef(new Set());
  const handlePreload = useCallback((file) => {
    if (!token || !file || preloadedRef.current.has(file)) return;
    preloadedRef.current.add(file);
    fetch(`${API}/api/preload?file=${encodeURIComponent(file)}`, { headers:{ Authorization:`Bearer ${token}` } }).catch(() => {});
  }, [token]);

  const sendPresencePing = useCallback(() => {
    if (!token) return;
    fetch(`${API}/api/presence/ping`, { method:'POST', headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${token}` }, body: JSON.stringify({ watching_file: watchingFile.current || '' }) }).catch(() => {});
  }, [token]);

  const showNotification = useCallback((data) => {
    clearTimeout(notifTimer.current);
    setNotification(data);
    notifTimer.current = setTimeout(() => setNotification(null), 6000);
  }, []);

  // SSE para notificaciones de nuevo contenido
  useEffect(() => {
    if (!token) return;
    return subscribeSSE(token, data => {
      if (data.type === 'new_content') { showNotification(data); reloadLibrary(); reloadSeries(); }
    });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar perfiles al restaurar sesión
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/profiles`, { headers:{ Authorization:`Bearer ${token}` } })
      .then(authJson)
      .then(profs => {
        if (!Array.isArray(profs) || profs.length === 0) return;
        setProfiles(profs);
        const savedId = parseInt(localStorage.getItem('profile_id') || '0');
        const saved = profs.find(p => p.id === savedId);
        if (saved) setSelectedProfile(saved);
      }).catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar contenido y géneros al seleccionar perfil
  useEffect(() => {
    if (!token || !selectedProfile) return;
    reloadLibrary(); reloadRatings(); reloadInfo(); reloadSeries(); reloadWatchlist();
    apiJson(`${API}/api/history`).then(d => { if (d) setHistory(d); }).catch(() => {});
    apiJson(`${API}/api/genres`).then(d => { if (Array.isArray(d)) setAvailableGenres(d); }).catch(() => {});
    sendPresencePing();
    presenceTimer.current = setInterval(sendPresencePing, PRESENCE_INTERVAL);
    return () => clearInterval(presenceTimer.current);
  }, [token, selectedProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (library.length > 0) reloadTranscodeStatus(); }, [library]);

  useEffect(() => {
    if (!token) return;
    const hasPending = Object.values(transcodeStatus).some(s => s === 'pending' || s === 'processing');
    if (!hasPending) return;
    const t = setInterval(reloadTranscodeStatus, 15000);
    return () => clearInterval(t);
  }, [transcodeStatus, token]);

  useEffect(() => { watchingFile.current = selected ? selected.file : ''; }, [selected]);

  useEffect(() => {
    if (!selected || !token) { setSelectedSubs([]); return; }
    fetch(`${API}/api/subtitles?file=${encodeURIComponent(selected.file)}`, { headers:{ Authorization:`Bearer ${token}` } })
      .then(authJson).then(d => setSelectedSubs(Array.isArray(d) ? d : [])).catch(() => setSelectedSubs([]));
  }, [selected, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Búsqueda server-side con debounce de 400ms
  useEffect(() => {
    if (!token || !selectedProfile) return;
    clearTimeout(searchTimer.current);

    if (!isSearchMode) {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    searchTimer.current = setTimeout(async () => {
      if (searchAbort.current) searchAbort.current.abort();
      const ctrl = new AbortController();
      searchAbort.current = ctrl;
      try {
        const params = new URLSearchParams();
        if (search) params.set('q', search);
        if (genreFilter) params.set('genre', genreFilter);
        if (yearFilter) params.set('year', yearFilter);
        if (filter !== 'all' && filter !== 'watchlist') params.set('type', filter);
        const res = await fetch(`${API}/api/search?${params}`, {
          headers: profileHeaders(),
          signal: ctrl.signal,
        });
        if (res.status === 401) { logout(); return; }
        if (res.ok) setSearchResults(await res.json());
        else setSearchResults({ movies: [], series: [] });
      } catch (e) {
        if (e.name !== 'AbortError') setSearchResults({ movies: [], series: [] });
      } finally {
        // Una búsqueda abortada no debe apagar el loading de la que la reemplazó.
        if (searchAbort.current === ctrl) setSearchLoading(false);
      }
    }, 400);
  }, [search, genreFilter, yearFilter, filter, token, selectedProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  // keepalive deja que la petición termine aunque la pestaña se cierre.
  // (Cuando se usa como handler de onPause/onEnded recibe el evento, que no trae keepalive.)
  const saveProgress = useCallback((opts) => {
    const v = videoRef.current;
    if (!v || !selected || v.currentTime < 5) return;
    const progress = v.currentTime, duration = v.duration || 0;
    fetch(`${API}/api/progress`, { method:'POST', keepalive: opts?.keepalive === true, headers: profileHeaders({ 'Content-Type':'application/json' }), body: JSON.stringify({ file_name:selected.file, progress, duration }) })
      .then(r => { if (r.ok) setHistory(prev => ({ ...prev, [selected.file]:{ progress, duration, updated: Date.now() / 1000 } })); })
      .catch(() => {});
  }, [selected, token, selectedProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selected) return;
    progressTimer.current = setInterval(saveProgress, 10000);
    return () => clearInterval(progressTimer.current);
  }, [selected, saveProgress]);

  // Cerrar la pestaña o mandar la app a segundo plano (móvil) no dispara onPause.
  useEffect(() => {
    if (!selected) return;
    const flush = () => saveProgress({ keepalive: true });
    const onVisibility = () => { if (document.visibilityState === 'hidden') flush(); };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [selected, saveProgress]);

  // Siguiente episodio de la serie que se está viendo (según temporada/episodio).
  const episodesBySeries = useMemo(() => groupEpisodes(library), [library]);
  const nextEpisode = useMemo(() => {
    if (!selected) return null;
    const current = library.find(m => m.file === selected.file);
    const eps = current && episodesBySeries[current.series_id];
    if (!eps) return null;
    const i = eps.findIndex(e => e.file === selected.file);
    return i >= 0 && i < eps.length - 1 ? eps[i + 1] : null;
  }, [selected, library, episodesBySeries]);

  const playNext = useCallback(() => {
    if (!nextEpisode) return;
    saveProgress();
    setSelected(nextEpisode);
  }, [nextEpisode, saveProgress]);

  const handleEnded = () => {
    saveProgress();
    if (!nextEpisode) return;
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    setUpNextSecs(10);
  };

  useEffect(() => { setUpNextSecs(null); }, [selected]);

  useEffect(() => {
    if (upNextSecs === null) return;
    if (upNextSecs <= 0) { playNext(); return; }
    const t = setTimeout(() => setUpNextSecs(s => (s === null ? null : s - 1)), 1000);
    return () => clearTimeout(t);
  }, [upNextSecs]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVideoLoad = () => {
    const v = videoRef.current;
    if (!v || !selected) return;
    const s = history[selected.file];
    if (s && s.progress > 10 && s.duration > 0 && (s.progress / s.duration) < 0.95) v.currentTime = s.progress;
  };

  // Con un modal abierto las teclas son del modal, no del video de atrás.
  const modalOpen = !!(detail || selectedSeries || showAdmin);
  useEffect(() => {
    if (!selected || modalOpen) return;
    const onKey = (e) => {
      if (['INPUT','TEXTAREA','SELECT'].includes(e.target.tagName) || e.target.isContentEditable) return;
      const v = videoRef.current;
      if (!v) return;
      if (e.code === 'Space')       { if (e.target.tagName === 'BUTTON') return; e.preventDefault(); v.paused ? v.play() : v.pause(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); v.currentTime = Math.min(v.duration || Infinity, v.currentTime + 10); }
      else if (e.key === 'ArrowLeft')  { e.preventDefault(); v.currentTime = Math.max(0, v.currentTime - 10); }
      else if (e.key === 'ArrowUp')    { e.preventDefault(); v.volume = Math.min(1, v.volume + 0.1); }
      else if (e.key === 'ArrowDown')  { e.preventDefault(); v.volume = Math.max(0, v.volume - 0.1); }
      else if (e.key === 'f' || e.key === 'F') { e.preventDefault(); document.fullscreenElement ? document.exitFullscreen() : v.requestFullscreen().catch(() => {}); }
      else if (e.key === 'm' || e.key === 'M') { e.preventDefault(); v.muted = !v.muted; }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selected, videoRef, modalOpen]);

  const formatTime = secs => {
    if (!secs) return '';
    const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = Math.floor(secs % 60);
    return h > 0 ? `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}` : `${m}:${String(s).padStart(2,'0')}`;
  };

  const reloadProfiles = useCallback(() => {
    fetch(`${API}/api/profiles`, { headers:{ Authorization:`Bearer ${token}` } })
      .then(authJson).then(profs => { if (Array.isArray(profs)) setProfiles(profs); }).catch(() => {});
  }, [token, authJson]);

  const handleMobileFilter = useCallback((f) => {
    setFilter(f);
    setShowMobileSearch(false);
  }, []);

  const clearFilters = () => { setGenreFilter(''); setYearFilter(''); };

  const toggleMobileSearch = () => {
    if (showMobileSearch) {
      setShowMobileSearch(false);
      setSearch('');
      clearFilters();
    } else {
      setShowMobileSearch(true);
    }
  };

  if (!token) return <AuthScreen onAuth={onAuth} />;

  if (profiles.length > 0 && !selectedProfile) {
    return (
      <ProfileSelector
        token={token}
        profiles={profiles}
        onSelect={handleSelectProfile}
        onProfilesChange={reloadProfiles}
      />
    );
  }

  const filtered = library.filter(m => {
    if (m.series_id > 0 && filter !== 'all' && filter !== 'series') return false;
    if (m.series_id > 0 && filter === 'all') return false;
    return filter === 'all' || m.type === filter;
  });
  const filteredSeries = seriesList;
  const movies         = filtered.filter(m => m.type === 'movie');
  const seriesFiles    = filtered.filter(m => m.type === 'series');
  const byGenre        = {};
  movies.forEach(m => { const g = mediaInfo[m.file]?.genre || ''; if (g) { if (!byGenre[g]) byGenre[g] = []; byGenre[g].push(m); } });
  const hasGenres      = Object.keys(byGenre).length > 0;
  const watchlistMedia  = library.filter(m => watchlist.has(m.file));
  const watchlistSeries = seriesList.filter(s => watchlist.has(`series_${s.id}`));

  // Pills de géneros (reutilizados en header desktop y móvil)
  const genrePills = availableGenres.map(g => (
    <button key={g} onClick={() => setGenreFilter(g === genreFilter ? '' : g)} style={{
      background: g === genreFilter ? `linear-gradient(135deg,${C.accent},${C.accentSoft})` : C.accentDim,
      border: `1px solid ${g === genreFilter ? 'transparent' : C.accentBorder}`,
      color: g === genreFilter ? '#fff' : C.textMuted,
      padding: '5px 12px', borderRadius: '20px', cursor: 'pointer',
      fontSize: '12px', fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap',
      flexShrink: 0, WebkitTapHighlightColor: 'transparent',
    }}>{g}</button>
  ));

  const yearInput = (
    <input
      type="number"
      value={yearFilter}
      onChange={e => setYearFilter(e.target.value)}
      placeholder="Año"
      min="1900" max="2099"
      style={{ width: '70px', flexShrink: 0, background: 'rgba(255,255,255,0.04)', border: `1px solid ${yearFilter ? C.accent : C.accentBorder}`, color: C.text, padding: '5px 10px', borderRadius: '20px', fontSize: '12px', fontFamily: 'Inter, sans-serif', outline: 'none', appearance: 'textfield' }}
    />
  );

  const clearBtn = (genreFilter || yearFilter) && (
    <button onClick={clearFilters} style={{ flexShrink: 0, background: 'rgba(192,57,43,0.15)', border: `1px solid rgba(192,57,43,0.3)`, color: '#e74c3c', padding: '5px 12px', borderRadius: '20px', cursor: 'pointer', fontSize: '12px', fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap', WebkitTapHighlightColor: 'transparent' }}>
      ✕ Limpiar
    </button>
  );

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text }}>
      {/* Toast de nuevo contenido */}
      {notification && (
        <div style={{ position:'fixed', top:'70px', right:'20px', zIndex:500, background:C.bgCard, border:`1px solid ${C.accentBorder}`, borderRadius:'12px', padding:'14px 18px', maxWidth:'320px', boxShadow:'0 8px 32px rgba(0,0,0,0.6)' }}>
          <p style={{ color:C.accentSoft, fontSize:'12px', fontWeight:'600', marginBottom:'4px', fontFamily:'Inter, sans-serif' }}>Nuevo contenido disponible</p>
          <p style={{ color:C.text, fontSize:'13px', fontFamily:'Inter, sans-serif', wordBreak:'break-word' }}>{notification.file}</p>
          <button onClick={() => setNotification(null)} style={{ position:'absolute', top:'8px', right:'10px', background:'transparent', border:'none', color:C.textMuted, cursor:'pointer', fontSize:'14px', padding:'2px 4px' }}>✕</button>
        </div>
      )}

      {detail && <DetailModal media={detail} info={mediaInfo[detail.file]} rating={ratings[detail.file]} apiJson={apiJson} onPlay={m => { saveProgress(); setSelected(m); window.scrollTo({ top:0, behavior:'smooth' }); }} onClose={() => setDetail(null)} onRated={reloadRatings} />}
      {selectedSeries && <SeriesModal series={selectedSeries} apiJson={apiJson} isMobile={isMobile} onClose={() => setSelectedSeries(null)} onPlay={m => { saveProgress(); setSelected(m); window.scrollTo({ top:0, behavior:'smooth' }); }} />}
      {showAdmin && user?.role === 'admin'    && <AdminPanel    token={token} user={user} onClose={() => { setShowAdmin(false); reloadLibrary(); reloadInfo(); }} onUploadDone={reloadLibrary} onSessionExpired={() => { setShowAdmin(false); logout(); }} />}
      {showAdmin && user?.role === 'uploader' && <UploaderPanel token={token} onClose={() => setShowAdmin(false)} />}
      {user?.role !== 'admin' && <ChatBubble token={token} user={user} />}

      {/* Header */}
      <header style={{ background:C.bgHeader, padding: isMobile?'10px 14px':isTV?'20px 80px':'16px 52px', display:'flex', alignItems:'center', gap: isMobile?'10px':'28px', position:'sticky', top:0, zIndex:200, borderBottom:`1px solid ${C.border}`, backdropFilter:'blur(16px)', flexWrap:'nowrap', overflow:'hidden' }}>
        <h1 style={{ fontFamily:'Playfair Display, serif', color:C.accentSoft, fontWeight:'700', letterSpacing:'2px', fontSize: isMobile?'18px':isTV?'32px':'24px', flexShrink:0 }}>TLALLI</h1>

        {!isMobile && (
          <div style={{ display:'flex', gap:'5px', flexShrink:0 }}>
            {['all','movie','series'].map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{ background:filter===f?`linear-gradient(135deg,${C.accent},${C.accentSoft})`:C.accentDim, border:`1px solid ${filter===f?'transparent':C.accentBorder}`, color:filter===f?'#fff':C.textMuted, padding:isTV?'10px 26px':'7px 14px', borderRadius:'20px', cursor:'pointer', fontSize:isTV?'15px':'13px', fontFamily:'Inter, sans-serif', fontWeight:'500', whiteSpace:'nowrap' }}>
                {f==='all'?'Todo':f==='movie'?'Films':'Series'}
              </button>
            ))}
          </div>
        )}

        {/* Buscador desktop */}
        {!isMobile && (
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar título, género, director…"
            style={{ marginLeft:'auto', background:'rgba(255,255,255,0.04)', border:`1px solid ${search ? C.accent : C.accentBorder}`, color:C.text, padding:isTV?'12px 24px':'8px 16px', borderRadius:'20px', fontSize:isTV?'15px':'13px', outline:'none', width:isTV?'300px':'210px', fontFamily:'Inter, sans-serif', flexShrink:1, minWidth:'100px', transition:'border-color 0.2s' }}
          />
        )}

        {/* Lupa + avatar en móvil */}
        {isMobile && <div style={{ flex: 1 }} />}
        {isMobile && (
          <button onClick={toggleMobileSearch} style={{ background: showMobileSearch ? 'rgba(201,113,74,0.15)' : 'transparent', border: 'none', padding: '7px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: showMobileSearch ? C.accentSoft : C.textMuted, flexShrink: 0, WebkitTapHighlightColor: 'transparent' }}>
            <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>
          </button>
        )}

        <div style={{ flexShrink: 0 }}>
          <UserMenu user={user} profile={selectedProfile} profiles={profiles} isMobile={isMobile} onSwitchProfile={() => setSelectedProfile(null)} onAdmin={() => setShowAdmin(true)} onLogout={logout} onInstall={handleInstall} installPrompt={installPrompt} />
        </div>
      </header>

      {/* Búsqueda + filtros en móvil (desplegable) */}
      {isMobile && showMobileSearch && (
        <div style={{ background: C.bgCard, borderBottom: `1px solid ${C.border}` }}>
          <div style={{ padding: '8px 12px 8px' }}>
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar título, género, director…"
              style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: `1px solid ${search ? C.accent : C.accentBorder}`, color: C.text, padding: '9px 14px', borderRadius: '20px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: 'Inter, sans-serif' }}
            />
          </div>
          {availableGenres.length > 0 && (
            <div style={{ padding: '0 12px 10px', display: 'flex', gap: '6px', overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', alignItems: 'center' }}>
              {genrePills}
              {yearInput}
              {clearBtn}
            </div>
          )}
        </div>
      )}

      {/* Barra de filtros desktop — visible al buscar */}
      {!isMobile && isSearchMode && availableGenres.length > 0 && (
        <div style={{ padding: '8px 52px', background: C.bgCard, borderBottom: `1px solid ${C.border}`, display: 'flex', gap: '6px', alignItems: 'center', overflowX: 'auto', scrollbarWidth: 'none' }}>
          {genrePills}
          {yearInput}
          {clearBtn}
        </div>
      )}

      {/* Reproductor */}
      {selected && (
        <div style={{ background: '#080402', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile?'14px':isTV?'40px 80px':'28px 52px' }}>
            <div style={{ position:'relative' }}>
            <VideoPlayer
              key={selected.file}
              videoRef={videoRef}
              src={`${API}/stream/${encodeURIComponent(selected.file)}`}
              hlsBase={transcodeStatus[selected.file] === 'done' ? encodeURIComponent(selected.file.replace(/\.[^/.]+$/, '')) : null}
              transcodeStatus={transcodeStatus[selected.file]}
              onLoadedMetadata={handleVideoLoad}
              onPause={saveProgress}
              onEnded={handleEnded}
              subtitles={selectedSubs}
              style={{ width:'100%', maxHeight:isTV?'720px':'560px', borderRadius:isMobile?'10px':'14px', background:'#000', display:'block', boxShadow:'0 24px 64px rgba(0,0,0,0.8)' }}
            />
            {upNextSecs !== null && nextEpisode && (
              <div style={{ position:'absolute', right:isMobile?'10px':'20px', bottom:isMobile?'50px':'70px', zIndex:30, background:'rgba(8,4,2,0.92)', border:`1px solid ${C.accentBorder}`, borderRadius:'12px', padding:isMobile?'10px 12px':'14px 18px', maxWidth:'320px', boxShadow:'0 8px 32px rgba(0,0,0,0.6)', fontFamily:'Inter, sans-serif' }}>
                <p style={{ color:C.textMuted, fontSize:'11px', marginBottom:'4px' }}>Siguiente episodio en {upNextSecs} s</p>
                <p style={{ color:C.text, fontSize:'13px', fontWeight:'600', marginBottom:'10px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>T{nextEpisode.season}E{String(nextEpisode.episode).padStart(2,'0')} · {mediaInfo[nextEpisode.file]?.title || nextEpisode.name}</p>
                <div style={{ display:'flex', gap:'8px' }}>
                  <button autoFocus onClick={playNext} style={{ flex:1, background:`linear-gradient(135deg,${C.accent},${C.accentSoft})`, border:'none', color:'#fff', padding:'8px 14px', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'600' }}>▶ Reproducir</button>
                  <button onClick={() => setUpNextSecs(null)} style={{ background:'transparent', border:`1px solid ${C.accentBorder}`, color:C.textMuted, padding:'8px 14px', borderRadius:'8px', cursor:'pointer', fontSize:'13px' }}>Cancelar</button>
                </div>
              </div>
            )}
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'14px', flexWrap:'wrap', gap:'10px' }}>
              <div>
                <h2 style={{ fontFamily:'Playfair Display, serif', fontSize:isMobile?'15px':isTV?'30px':'20px', fontWeight:'600', color:C.text }}>{mediaInfo[selected.file]?.title || selected.name}</h2>
                <div style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:'5px', flexWrap:'wrap' }}>
                  <span style={{ background:C.accentDim, color:C.accentSoft, fontSize:'10px', padding:'2px 8px', borderRadius:'20px', border:`1px solid ${C.accentBorder}` }}>{selected.type==='movie'?'Película':'Serie'}</span>
                  {transcodeStatus[selected.file]==='done' && <span style={{ background:'rgba(93,173,226,0.15)', color:'#5dade2', fontSize:'10px', padding:'2px 8px', borderRadius:'20px', border:'1px solid rgba(93,173,226,0.35)', fontWeight:'600' }}>HD</span>}
                  {(transcodeStatus[selected.file]==='pending'||transcodeStatus[selected.file]==='processing') && <span style={{ background:'rgba(240,192,64,0.12)', color:'#f0c040', fontSize:'10px', padding:'2px 8px', borderRadius:'20px', border:'1px solid rgba(240,192,64,0.3)' }}>⚙ Procesando HD…</span>}
                  {mediaInfo[selected.file]?.genre && <span style={{ color:C.textMuted, fontSize:'12px' }}>{mediaInfo[selected.file].genre}</span>}
                  {history[selected.file] && <span style={{ color:C.accentSoft, fontSize:'12px' }}>▶ Desde {formatTime(history[selected.file].progress)}</span>}
                  {selectedSubs.length > 0 && <span style={{ color:C.textMuted, fontSize:'11px' }}>🔤 {selectedSubs.length} sub{selectedSubs.length > 1 ? 's' : ''}</span>}
                </div>
              </div>
              <div style={{ display:'flex', gap:'8px' }}>
              {nextEpisode && <button onClick={playNext} style={{ background:C.accentDim, border:`1px solid ${C.accentBorder}`, color:C.accentSoft, padding:isMobile?'7px 14px':'9px 20px', borderRadius:'10px', cursor:'pointer', fontSize:isMobile?'12px':'13px' }}>Siguiente episodio ▶</button>}
              <button onClick={() => { saveProgress(); setSelected(null); }} style={{ background:'transparent', border:`1px solid ${C.accentBorder}`, color:C.textMuted, padding:isMobile?'7px 14px':'9px 20px', borderRadius:'10px', cursor:'pointer', fontSize:isMobile?'12px':'13px' }}>✕ Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contenido principal */}
      <main style={{
        padding: isMobile ? '20px 12px' : isTV ? '52px 80px' : '40px 52px',
        paddingBottom: isMobile ? 'calc(76px + env(safe-area-inset-bottom))' : isTV ? '52px' : '40px',
        maxWidth: '2000px',
        margin: '0 auto',
      }}>
        {/* Modo búsqueda — resultados del servidor */}
        {isSearchMode ? (
          <SearchResults
            results={searchResults}
            loading={searchLoading}
            query={search}
            genreFilter={genreFilter}
            yearFilter={yearFilter}
            library={library}
            seriesList={seriesList}
            mediaInfo={mediaInfo}
            history={history}
            ratings={ratings}
            isMobile={isMobile}
            isTablet={isTablet}
            isTV={isTV}
            cols={cols}
            selected={selected}
            hovered={hovered}
            setHovered={setHovered}
            onCardClick={setDetail}
            onSeriesClick={setSelectedSeries}
            watchlist={watchlist}
            toggleWatchlist={toggleWatchlist}
          />
        ) : filter === 'watchlist' ? (
          /* Tab Mi Lista */
          watchlistMedia.length === 0 && watchlistSeries.length === 0 ? (
            <div style={{ textAlign:'center', color:C.textDim, padding:'80px 0' }}>
              <p style={{ fontSize:'48px', marginBottom:'16px' }}>🔖</p>
              <p style={{ fontFamily:'Playfair Display, serif', fontSize:'20px', color:C.textMuted, marginBottom:'8px' }}>Tu lista está vacía</p>
              <p style={{ fontSize:'13px' }}>Agrega películas y series pulsando el ❤</p>
            </div>
          ) : (
            <>
              {watchlistMedia.length > 0 && <Section title="Guardados" items={watchlistMedia} history={history} ratings={ratings} mediaInfo={mediaInfo} selected={selected} hovered={hovered} setHovered={setHovered} isMobile={isMobile} isTablet={isTablet} isTV={isTV} cols={cols} onCardClick={setDetail} watchlist={watchlist} toggleWatchlist={toggleWatchlist} onPreload={handlePreload} />}
              {watchlistSeries.length > 0 && <SeriesRow seriesList={watchlistSeries} isMobile={isMobile} isTV={isTV} onSeriesClick={setSelectedSeries} watchlist={watchlist} toggleWatchlist={toggleWatchlist} />}
            </>
          )
        ) : (
          /* Navegación normal */
          <>
            {filter==='all' && !selected && (
              <ContinueRow library={library} history={history} mediaInfo={mediaInfo} isMobile={isMobile} isTV={isTV} onPlay={setSelected} />
            )}
            {filter==='all' && (watchlistMedia.length > 0 || watchlistSeries.length > 0) && (
              <WatchlistRow mediaItems={watchlistMedia} seriesItems={watchlistSeries} isMobile={isMobile} isTV={isTV} mediaInfo={mediaInfo} history={history} onCardClick={setDetail} onSeriesClick={setSelectedSeries} watchlist={watchlist} toggleWatchlist={toggleWatchlist} />
            )}
            {filtered.length === 0 ? (
              <div style={{ textAlign:'center', color:C.textDim, padding:'100px 0' }}>
                <p style={{ fontSize:'52px', marginBottom:'20px' }}>🎬</p>
                <p style={{ fontFamily:'Playfair Display, serif', fontSize:'22px', color:C.textMuted }}>Sin contenido disponible</p>
                <p style={{ fontSize:'14px', marginTop:'10px' }}>Agrega videos desde el panel de administración</p>
              </div>
            ) : hasGenres && filter==='all' ? (
              <>
                {Object.entries(byGenre).sort(([a],[b]) => a.localeCompare(b)).map(([g, items]) => (
                  <GenreRow key={g} genre={g} items={items} isMobile={isMobile} isTV={isTV} mediaInfo={mediaInfo} history={history} ratings={ratings} onCardClick={setDetail} watchlist={watchlist} toggleWatchlist={toggleWatchlist} onPreload={handlePreload} />
                ))}
                {(() => { const sg = filtered.filter(m => !mediaInfo[m.file]?.genre); return sg.length > 0 ? <Section title="Sin categoría" items={sg} history={history} ratings={ratings} mediaInfo={mediaInfo} selected={selected} hovered={hovered} setHovered={setHovered} isMobile={isMobile} isTablet={isTablet} isTV={isTV} cols={cols} onCardClick={setDetail} watchlist={watchlist} toggleWatchlist={toggleWatchlist} onPreload={handlePreload} /> : null; })()}
              </>
            ) : (
              filter==='series' ? (
                <>
                  <SeriesRow seriesList={filteredSeries} isMobile={isMobile} isTV={isTV} onSeriesClick={setSelectedSeries} watchlist={watchlist} toggleWatchlist={toggleWatchlist} />
                  {seriesFiles.length > 0 && <Section title="Sin asignar" items={seriesFiles} history={history} ratings={ratings} mediaInfo={mediaInfo} selected={selected} hovered={hovered} setHovered={setHovered} isMobile={isMobile} isTablet={isTablet} isTV={isTV} cols={cols} onCardClick={setDetail} watchlist={watchlist} toggleWatchlist={toggleWatchlist} onPreload={handlePreload} />}
                </>
              ) : filter==='all' ? (
                <>
                  <Section title="Películas" items={movies} history={history} ratings={ratings} mediaInfo={mediaInfo} selected={selected} hovered={hovered} setHovered={setHovered} isMobile={isMobile} isTablet={isTablet} isTV={isTV} cols={cols} onCardClick={setDetail} watchlist={watchlist} toggleWatchlist={toggleWatchlist} onPreload={handlePreload} />
                  <SeriesRow seriesList={filteredSeries} isMobile={isMobile} isTV={isTV} onSeriesClick={setSelectedSeries} watchlist={watchlist} toggleWatchlist={toggleWatchlist} />
                  {seriesFiles.length > 0 && <Section title="Series sin asignar" items={seriesFiles} history={history} ratings={ratings} mediaInfo={mediaInfo} selected={selected} hovered={hovered} setHovered={setHovered} isMobile={isMobile} isTablet={isTablet} isTV={isTV} cols={cols} onCardClick={setDetail} watchlist={watchlist} toggleWatchlist={toggleWatchlist} onPreload={handlePreload} />}
                </>
              ) : (
                <Section title="Películas" items={movies} history={history} ratings={ratings} mediaInfo={mediaInfo} selected={selected} hovered={hovered} setHovered={setHovered} isMobile={isMobile} isTablet={isTablet} isTV={isTV} cols={cols} onCardClick={setDetail} watchlist={watchlist} toggleWatchlist={toggleWatchlist} onPreload={handlePreload} />
              )
            )}
          </>
        )}
      </main>

      {!isMobile && (
        <footer style={{ borderTop:`1px solid ${C.border}`, padding:'24px 52px', display:'flex', justifyContent:'center', color:C.textDim, fontSize:'12px', letterSpacing:'1px' }}>
          © 2026 TLALLI · Cinema en casa
        </footer>
      )}

      {isMobile && <BottomNav filter={filter} onFilter={handleMobileFilter} />}
    </div>
  );
}
